/**
 * ATTEST-REQUEST-COACH-1 — the request box must not dead-end.
 *
 * A layperson types "confirm the tax info is correct", the deny list fires, and
 * the old behaviour put a full-width `RefusalPanel` under Step 1: a red wall
 * with a code chip, telling them the whole engagement is impossible. It was not
 * wrong — that ask genuinely cannot be issued — it was just the end of the road
 * for someone whose *next* sentence would have been fine.
 *
 * So the same finding is shown against the line that caused it, with the
 * profession's own permitted wording next to it and a button that swaps it in.
 * The wall is still there, and still reachable: it renders the moment there is
 * nothing left to suggest, or the moment the CPA turns every suggestion down.
 *
 * Three things this deliberately does NOT do:
 *
 * 1. **It does not decide anything.** Every card is built from the routing
 *    decision the server returned — the quoted line, the rationale and the
 *    replacement are all `excluded_items[].hits[]` fields. There is no local
 *    copy of the deny list here, exactly as there is none in `RefusalPanel`.
 * 2. **It does not clear the block itself.** "Use this wording" edits the
 *    textarea and re-runs `/classify`; the block lifts only because the engine
 *    re-evaluated the new text and stopped refusing. A rewrite that still
 *    tripped a guardrail would simply come back as another card. The button is
 *    a typing shortcut, not a bypass.
 * 3. **It does not widen what may be said.** `safe_alternative` is a *different,
 *    permitted* statement the policy authored — it is not the denied assertion
 *    with softer words. Every assertion prohibited before this component existed
 *    is prohibited after it.
 *
 * Amber, not red: a rewritable line is a wording problem mid-edit, not a
 * failure. Red stays reserved for the refusal this escalates to.
 */
import { useEffect, useState } from 'react'

import type { AttestRefusal, ExcludedItem, RoutingDecision } from '@/components/attest/api'
import { RefusalPanel } from '@/components/attest/RefusalPanel'
import { guidanceFor, type AttestStepKey } from '@/components/attest/refusalGuidance'
import { Button } from '@/components/ui/button'

/** The DOM id of the request textarea in `ClassifyStep`. */
const REQUEST_BOX = 'requested-items'

/**
 * The engine's replacement for this line, or null if it did not offer one.
 *
 * First hit wins where several fired. The others' rationales are still shown —
 * the CPA should see everything wrong with the line — but only one replacement
 * can be written into a single line, and a rewrite that clears one guardrail
 * and not another comes straight back from the next `/classify` as a fresh
 * card. That is the loop working, not a gap in it.
 */
export function rewriteFor(item: ExcludedItem): string | null {
  if (item.check_unavailable) return null
  const hit = item.hits?.find(h => (h.safe_alternative ?? '').trim())
  return hit ? hit.safe_alternative.trim() : null
}

/**
 * Items this component can actually coach: the deny list fired, and the policy
 * published something the CPA may say in place of it.
 *
 * `review_items` never appear here. Those are "the compliance check could not
 * run" — unverified, not prohibited — and there is no permitted rewrite to
 * offer for a line nobody has read yet. Suggesting one would invent an
 * all-clear the engine never gave.
 */
export function coachableItems(decision: RoutingDecision | null): ExcludedItem[] {
  return (decision?.excluded_items ?? []).filter(i => rewriteFor(i) !== null)
}

/**
 * Swap one line of the textarea, matching the way the box is read.
 *
 * `splitRequestedItems` trims each line before sending it, so the server's
 * `requested_text` is the trimmed form and has to be compared as such. Only the
 * first match is replaced: two identical lines are two identical asks, and the
 * second one should come back on the next classify rather than be silently
 * rewritten by a click aimed at the first.
 */
export function replaceRequestLine(
  text: string,
  original: string,
  replacement: string,
): string {
  const target = original.trim()
  let done = false
  return text
    .split('\n')
    .map(line => {
      if (done || line.trim() !== target) return line
      done = true
      return replacement
    })
    .join('\n')
}

export interface RequestLineCoachProps {
  /** The refusal to fall back to once coaching is exhausted. */
  refusal: AttestRefusal
  /** The decision the refusal was derived from — the source of every card. */
  decision: RoutingDecision | null
  /** Live textarea contents, so a line the CPA already edited stops coaching. */
  requestedItemsText: string
  onUseWording: (original: string, replacement: string) => void
  /** Re-runs `/classify`. The engine, not this component, lifts the block. */
  onReclassify: () => void
  /**
   * The wizard's real navigation (`LetterGenerator`'s `goToStep`).
   *
   * It has to be the step-changing one, not the focus-only `onCorrect` this
   * used to take (Bundle 2 item 3). Several codes that arrive from `/classify`
   * are attributed by `refusalGuidance` to a *later* step —
   * `ATTEST_ASSERTION_DENIED` to "3. Letter and statements" — so a handler that
   * swallowed the step left the CPA on step 1 pressing a button that named
   * step 3 and did nothing at all.
   */
  onGoToStep?: (step: AttestStepKey, fieldAnchor?: string) => void
}

export function RequestLineCoach({
  refusal, decision, requestedItemsText, onUseWording, onReclassify, onGoToStep,
}: RequestLineCoachProps) {
  /** Lines the CPA has explicitly kept. Cleared whenever a new decision lands. */
  const [dismissed, setDismissed] = useState<string[]>([])
  /**
   * The textarea contents at the moment "Use this wording" was pressed.
   *
   * The re-classify cannot be fired from the click handler: `onReclassify`
   * closes over the form state of the render it was created in, so calling it
   * in the same tick as the edit would send the *old* text to the server and
   * refuse again on a line that is no longer there. Waiting for the changed
   * value to arrive as a prop is what guarantees the request describes what is
   * actually in the box.
   */
  const [appliedAt, setAppliedAt] = useState<string | null>(null)

  useEffect(() => { setDismissed([]) }, [decision])

  useEffect(() => {
    if (appliedAt === null || requestedItemsText === appliedAt) return
    setAppliedAt(null)
    onReclassify()
  }, [requestedItemsText, appliedAt, onReclassify])

  const present = new Set(
    requestedItemsText.split('\n').map(l => l.trim()).filter(Boolean),
  )
  const open = coachableItems(decision).filter(
    i => !dismissed.includes(i.requested_text) && present.has(i.requested_text.trim()),
  )

  /**
   * Nothing left to suggest — every line is un-rewritable, already edited away,
   * or was turned down. This is the "warm, field-anchored" wall: same panel,
   * same code, same policy text, but now it can put the cursor back in the box
   * it is about.
   *
   * The anchor is passed from here rather than added to `refusalGuidance`'s
   * table. That table leaves `ATTEST_ENGAGEMENT_REFUSED` unanchored on purpose
   * — the code can be raised by several inputs, and a cursor dropped in the
   * wrong box is a wrong signpost. This call site is the one place that
   * ambiguity is resolved: coaching only exhausts *after* working through the
   * request lines, so here the refusal demonstrably is about the request box.
   *
   * But only for a code the table itself puts on this step. Where the guidance
   * names a later step, the request box is not what the CPA is being sent to,
   * and pinning the cursor there would fight the very jump it is attached to.
   */
  if (open.length === 0) {
    const onClassifyStep = guidanceFor(refusal.error_code)?.step === 'classify'
    return (
      <RefusalPanel
        refusal={refusal}
        onGoToStep={onGoToStep}
        fieldAnchor={onClassifyStep ? REQUEST_BOX : undefined}
      />
    )
  }

  const stuck = (decision?.excluded_items ?? []).filter(i => rewriteFor(i) === null)

  return (
    <div
      data-testid="request-line-coach"
      className="rounded-lg border border-amber-300 bg-amber-50 p-4"
    >
      <h3 className="text-sm font-semibold text-amber-900">
        {open.length === 1
          ? 'One line has to be reworded before this can be issued'
          : `${open.length} lines have to be reworded before this can be issued`}
      </h3>
      <p className="mt-1 text-sm text-amber-900/90">
        A CPA may not attest to what these ask for. Each one has a permitted
        statement below that covers the same ground — use it, or keep your own
        wording and the request stands as refused.
      </p>

      <ul className="mt-3 space-y-3">
        {open.map((item, i) => {
          const replacement = rewriteFor(item) as string
          return (
            <li
              key={item.requested_text}
              data-testid={`coach-line-${i}`}
              className="rounded border border-amber-300 bg-white p-3"
            >
              <p className="text-sm italic text-gray-800">
                &ldquo;{item.requested_text}&rdquo;
              </p>

              {item.hits.map((h, j) => (
                <p key={j} className="mt-2 text-sm text-amber-900">
                  <span className="font-mono text-xs">{h.assertion_id}</span>{' '}
                  {h.rationale}
                </p>
              ))}

              <p className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <span className="font-semibold">You may say instead: </span>
                {replacement}
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  data-testid={`coach-use-${i}`}
                  onClick={() => {
                    setAppliedAt(requestedItemsText)
                    onUseWording(item.requested_text, replacement)
                  }}
                >
                  Use this wording
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`coach-dismiss-${i}`}
                  onClick={() => setDismissed(prev => [...prev, item.requested_text])}
                >
                  Keep my wording
                </Button>
              </div>
            </li>
          )
        })}
      </ul>

      {/* Listed, not hidden: a line with no permitted equivalent is the reason
          this request may end up refused anyway, and the CPA should be able to
          see it before spending clicks on the ones that can be fixed. */}
      {stuck.length > 0 && (
        <div className="mt-3" data-testid="coach-unrewritable">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-900/80">
            No permitted equivalent — these have to be dropped
          </p>
          <ul className="mt-1 list-disc pl-5 text-sm text-gray-800">
            {stuck.map((item, i) => <li key={i}>&ldquo;{item.requested_text}&rdquo;</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}
